import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PhoneCall, Loader2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const MemberCallsDialog = ({ member, onClose }) => {
  const navigate = useNavigate();
  const open = (c) => { onClose(); navigate(`/admin/${c.parent_type === 'company' ? 'companies' : 'contacts'}?open=${c.parent_id}`); };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="member-calls-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Calls by {member?.name}</DialogTitle>
          <DialogDescription className="text-slate-400">{member?.calls} calls · {member?.connection_rate}% connected</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(member?.items || []).map((c) => (
            <button key={c.id} onClick={() => open(c)} data-testid={`member-call-${c.id}`}
              className="w-full text-left flex items-center justify-between gap-3 border border-slate-800 rounded-lg px-3 py-2 hover:border-[#00A79D] transition-colors">
              <span className="min-w-0">
                <span className="text-sm font-semibold text-white inline-flex items-center gap-1">{c.record_name} <ExternalLink className="w-3 h-3 opacity-50" /></span>
                <span className="block text-xs text-slate-400 truncate">{(c.body || '').split('\n')[0]}</span>
              </span>
              <span className="text-right shrink-0">
                <span className={`block text-xs font-semibold ${c.outcome === 'connected' ? 'text-emerald-400' : 'text-slate-300'}`}>{c.outcome_label}</span>
                <span className="block text-[11px] text-slate-500">{fmt(c.occurred_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CallStatsPanel({ days }) {
  const [data, setData] = useState(null);
  const [member, setMember] = useState(null);
  useEffect(() => {
    axios.get(`${API}/activities/call-stats`, { params: { days } }).then((r) => setData(r.data)).catch(() => setData({ members: [], total: {} }));
  }, [days]);

  const t = data?.total || {};
  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="call-stats-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-[#00A79D]" /> Calls by team member <span className="text-xs text-slate-500 font-normal">last {days} days</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {!data ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          : data.members.length === 0 ? <p className="text-sm text-slate-500 px-4 py-6" data-testid="call-stats-empty">No calls logged yet. Tap a phone number in any list to log one.</p>
          : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left py-2 px-4 font-medium">Team member</th>
                <th className="text-right py-2 px-3 font-medium">Calls</th>
                <th className="text-right py-2 px-3 font-medium">Connected</th>
                <th className="text-right py-2 px-3 font-medium">Voicemail</th>
                <th className="text-right py-2 px-3 font-medium">No answer</th>
                <th className="text-right py-2 px-4 font-medium">Connection rate</th>
              </tr></thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.name} onClick={() => setMember(m)} className="border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer" data-testid={`call-stats-row-${m.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <td className="py-2.5 px-4 text-white font-medium">{m.name}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{m.calls}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-400">{m.connected}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{m.voicemail}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{m.no_answer}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-semibold text-[#00A79D]">{m.connection_rate}%</td>
                  </tr>
                ))}
                <tr className="text-slate-300 font-semibold" data-testid="call-stats-total">
                  <td className="py-2.5 px-4">Team total</td>
                  <td className="py-2.5 px-3 text-right font-mono">{t.calls}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-emerald-400">{t.connected}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{t.voicemail}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{t.no_answer}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-[#00A79D]">{t.connection_rate}%</td>
                </tr>
              </tbody>
            </table></div>
          )}
      </CardContent>
      <MemberCallsDialog member={member} onClose={() => setMember(null)} />
    </Card>
  );
}
